import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { UserDetails, UserSummaryDetails } from 'tsconfig/types.d';
import { userStatus } from '../helpers/user.utils';

interface UserState {
  user: UserDetails | undefined;
}

interface UserActions {
  setUser: (user: UserDetails | undefined) => void;
  reset: () => void;
}

export type UserStore = UserState & UserActions;

export const useUserStore = create<UserStore>()(
  devtools(
    set => ({
      user: undefined,
      setUser: user => set({ user }, undefined, 'setUser'),
      reset: () => set({ user: undefined }, undefined, 'reset'),
    }),
    { name: 'UserStore' },
  ),
);

// Derived selectors — pass to useUserStore() as selector argument
export const selectUserProfile = (state: UserState) => state.user;

export const selectUserDetails = (state: UserState): UserSummaryDetails | undefined => {
  const userData = state.user;
  const { loginStatus, loginType } = userStatus(userData?.securityStatus);
  return {
    firstName: userData?.summaryDetails?.firstName,
    lastName: userData?.summaryDetails?.lastName,
    salutation: userData?.summaryDetails?.salutation,
    profileId: userData?.summaryDetails?.profileId,
    brandAccountNumber: userData?.summaryDetails?.brandAccountNumber,
    wineFriendId: userData?.summaryDetails?.wineFriendId,
    businessPartner: userData?.summaryDetails?.businessPartner,
    emailAddress: userData?.summaryDetails?.emailAddress,
    brand: userData?.summaryDetails?.brand,
    loginStatus,
    loginType,
    shippingAddress: userData?.trustedDetails?.shippingAddress,
    billingAddress: userData?.trustedDetails?.billingAddress,
    subscriptions: userData?.trustedDetails?.subscriptions,
    paymentWallet: userData?.trustedDetails?.paymentWallet,
    userLoded: userData?.userLoded,
  } as UserSummaryDetails;
};

export const selectUserIsLoggedIn = (state: UserState) => state.user?.summaryDetails;

export const selectUserProfileClassification = (state: UserState) => state.user?.userDetails?.profileClassifications;

export const selectLoadedUser = (state: UserState) => state.user || undefined;

const EMPTY_ADDRESSES: readonly any[] = [];
export const selectUserDeliveryAddress = (state: UserState) =>
  state.user?.trustedDetails?.shippingAddress || EMPTY_ADDRESSES;

export const selectUserPointBalance = (state: UserState): number | undefined =>
  state.user?.userDetails?.velocitySSODetails?.pointsBalance;

export const selectUserIsUnlimited = (state: UserState): boolean => {
  const userData = state.user;
  const { subscriptions } = userData?.trustedDetails || {};

  if (subscriptions) {
    const result = subscriptions.find(
      ({ schemeTypes, type, status }) =>
        schemeTypes?.includes('FreeShip') && type === 'GENERAL' && (status === 'REQUESTED' || status === 'ACTIVE'),
    );
    return !!result;
  }
  return userData?.userDetails?.freeShipProfile ?? false;
};

// Pre-wrapped hooks — use these in components instead of useUserStore(selector)
export const useUserDetails = () => useUserStore(useShallow(selectUserDetails));
export const useUserProfile = () => useUserStore(selectUserProfile);
export const useUserIsLoggedIn = () => useUserStore(selectUserIsLoggedIn);
export const useUserIsUnlimited = () => useUserStore(selectUserIsUnlimited);
